import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

type Faq = {
  q: string;
  a: string;
  /** Tokens the answer must contain (used by post-deploy testid checks). */
  expect?: string[];
  /** Tokens the answer must NOT contain (used by post-deploy testid checks). */
  forbid?: string[];
};

const faqs: Faq[] = [
  {
    q: "$39 once — what's the catch? Is this really lifetime?",
    a: "No catch, no trial-to-subscription bait-and-switch. One payment of $39 (regular price $199) unlocks the platform forever, including every new source, scoring update, and blueprint format we ship. We make our money from volume, not from churn-resistant monthly billing.",
    expect: ["$39", "$199"],
    forbid: ["$79"],
  },
  {
    q: "Why so cheap when competitors charge $50–$200/month?",
    a: "Because we're not a research agency with account managers — we're a software product. The same automation that surfaces trends for you also lets us serve thousands of operators without scaling headcount. You get the output of a $200/mo tool at the price of a single month of one.",
    forbid: ["$79"],
  },
  {
    q: "What exactly do I get for $39? Spell it out.",
    a: "Unlimited access to the live trend feed (refreshed daily across 35+ sources), the full blueprint for every trend (buyer profile, price band, 3 product angles, supplier or template list, competition snapshot, 7-day launch plan), the monthly hit-rate report, every future source and feature, and email support. No seat limits, no usage caps, no add-ons. Normally $199 — you pay $39.",
    expect: ["$39", "$199"],
    forbid: ["$79"],
  },
  {
    q: "Are there any upsells, add-ons, or hidden fees later?",
    a: "Zero. There's no Pro tier, no per-trend unlock, no 'export credits,' no API metering. If we ever launch something genuinely new and separate (e.g. a done-for-you service), it'll be optional — your $39 license keeps getting everything in the core product.",
    expect: ["$39"],
    forbid: ["$79"],
  },
  {
    q: "What if it doesn't work for me?",
    a: "Use it for 14 days. If you don't surface at least one trend you'd genuinely build, email us and we refund the full $39 — no support tickets, no downgrade flow, no questions.",
    expect: ["$39"],
    forbid: ["$79"],
  },
];

const FAQ = () => (
  <section id="faq" className="py-32 border-t border-border" data-testid="faq-section">
    <div className="container max-w-3xl">
      <div className="text-center mb-16">
        <div className="font-mono text-xs text-primary mb-4">// OBJECTIONS, ANSWERED</div>
        <h2 className="font-display text-4xl md:text-6xl font-bold">
          You're skeptical. Good.
        </h2>
        <p className="mt-4 text-muted-foreground">
          The questions every smart operator asks before they buy.
        </p>
      </div>

      <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
        {faqs.map((f, i) => (
          <AccordionItem
            key={i}
            value={`item-${i}`}
            className="border border-border rounded-xl px-6 bg-card"
            data-testid={`faq-item-${i}`}
            data-faq-expect={(f.expect ?? []).join("|")}
            data-faq-forbid={(f.forbid ?? []).join("|")}
          >
            <AccordionTrigger
              className="text-left font-display font-semibold text-lg hover:no-underline py-5"
              data-testid={`faq-question-${i}`}
            >
              {f.q}
            </AccordionTrigger>
            <AccordionContent
              className="text-muted-foreground leading-relaxed pb-5"
              data-testid={`faq-answer-${i}`}
            >
              {f.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);


export default FAQ;
